// Central module registry — drives sidebar nav + records pages + form dialogs.
import {
  LayoutDashboard, GraduationCap, ClipboardCheck, ShieldCheck, PillBottle,
  AlertOctagon, MessageSquareWarning, Target, Wrench, Users, HeartHandshake,
} from "lucide-react";

export const MODULES = {
  training: {
    key: "training",
    label: "Staff Training Compliance",
    short: "Training",
    icon: GraduationCap,
    group: "Governance",
    fields: [
      { name: "staff_name", label: "Staff Name", type: "text", required: true },
      { name: "course", label: "Course / Topic", type: "text", required: true },
      { name: "provider", label: "Provider", type: "text" },
      { name: "completed_date", label: "Completed Date", type: "date", required: true },
      { name: "expiry_date", label: "Expiry Date", type: "date", required: true },
      { name: "certificate_id", label: "Certificate ID", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "staff_name", label: "Staff" },
      { key: "course", label: "Course" },
      { key: "provider", label: "Provider" },
      { key: "completed_date", label: "Completed" },
      { key: "expiry_date", label: "Expires", status: "expiry" },
    ],
  },
  supervisions: {
    key: "supervisions",
    label: "Supervision & Appraisals",
    short: "Supervision",
    icon: ClipboardCheck,
    group: "People",
    fields: [
      { name: "staff_name", label: "Staff Name", type: "text", required: true },
      { name: "kind", label: "Type", type: "select", options: ["supervision", "appraisal", "1-1"], required: true },
      { name: "scheduled_date", label: "Scheduled Date", type: "date", required: true },
      { name: "conducted_by", label: "Conducted By", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "completed", "overdue", "cancelled"], required: true },
      { name: "outcomes", label: "Outcomes / Development Actions", type: "textarea" },
    ],
    columns: [
      { key: "staff_name", label: "Staff" },
      { key: "kind", label: "Type" },
      { key: "scheduled_date", label: "Scheduled" },
      { key: "conducted_by", label: "Conducted By" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  audits: {
    key: "audits",
    label: "Audits & Action Plans",
    short: "Audits",
    icon: ShieldCheck,
    group: "Governance",
    aiEnabled: true,
    fields: [
      { name: "title", label: "Audit Title", type: "text", required: true },
      { name: "audit_type", label: "Audit Type", type: "select", options: ["medication", "infection_control", "care_plan", "health_safety", "documentation", "other"], required: true },
      { name: "conducted_date", label: "Conducted Date", type: "date", required: true },
      { name: "auditor", label: "Auditor", type: "text" },
      { name: "score", label: "Score (%)", type: "number" },
      { name: "kloes", label: "CQC KLOEs", type: "multiselect", options: ["Safe", "Effective", "Caring", "Responsive", "Well-led"] },
      { name: "status", label: "Status", type: "select", options: ["open", "in_progress", "closed"], required: true },
      { name: "findings", label: "Key Findings", type: "textarea", required: true },
      { name: "action_plan", label: "Action Plan", type: "textarea" },
    ],
    columns: [
      { key: "title", label: "Title" },
      { key: "audit_type", label: "Type" },
      { key: "conducted_date", label: "Date" },
      { key: "auditor", label: "Auditor" },
      { key: "score", label: "Score", numeric: true, suffix: "%" },
      { key: "kloes", label: "KLOEs" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  "medication-errors": {
    key: "medication-errors",
    label: "Medication Errors",
    short: "Medication",
    icon: PillBottle,
    group: "Clinical",
    aiEnabled: true,
    fields: [
      { name: "occurred_at", label: "Date of Error", type: "date", required: true },
      { name: "client_name", label: "Service User", type: "text", required: true },
      { name: "medication", label: "Medication", type: "text", required: true },
      { name: "error_type", label: "Error Type", type: "select", options: ["missed_dose", "wrong_dose", "wrong_med", "wrong_time", "wrong_route", "documentation", "other"], required: true },
      { name: "severity", label: "Severity", type: "select", options: ["low", "moderate", "high", "critical"], required: true },
      { name: "reported_by", label: "Reported By", type: "text" },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "immediate_action", label: "Immediate Action Taken", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["open", "reviewed", "closed"], required: true },
    ],
    columns: [
      { key: "occurred_at", label: "Date" },
      { key: "client_name", label: "Service User" },
      { key: "medication", label: "Medication" },
      { key: "error_type", label: "Type" },
      { key: "severity", label: "Severity", status: "severity" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  incidents: {
    key: "incidents",
    label: "Incidents & Safeguarding",
    short: "Incidents",
    icon: AlertOctagon,
    group: "Clinical",
    aiEnabled: true,
    fields: [
      { name: "occurred_at", label: "Date & Time", type: "datetime-local", required: true },
      { name: "category", label: "Category", type: "select", options: ["accident", "safeguarding", "near_miss", "behaviour", "injury", "fall", "other"], required: true },
      { name: "client_name", label: "Service User", type: "text" },
      { name: "location", label: "Location", type: "text" },
      { name: "severity", label: "Severity", type: "select", options: ["low", "moderate", "high", "critical"], required: true },
      { name: "reported_by", label: "Reported By", type: "text" },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "immediate_action", label: "Immediate Action", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["open", "under_investigation", "closed"], required: true },
    ],
    columns: [
      { key: "occurred_at", label: "When" },
      { key: "category", label: "Category" },
      { key: "client_name", label: "Service User" },
      { key: "severity", label: "Severity", status: "severity" },
      { key: "reported_by", label: "Reported By" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  complaints: {
    key: "complaints",
    label: "Complaints & Compliments",
    short: "Feedback",
    icon: MessageSquareWarning,
    group: "Governance",
    aiEnabled: true,
    fields: [
      { name: "kind", label: "Kind", type: "select", options: ["complaint", "compliment"], required: true },
      { name: "received_date", label: "Received Date", type: "date", required: true },
      { name: "source", label: "From (Name / Role)", type: "text", required: true },
      { name: "subject", label: "Subject", type: "text", required: true },
      { name: "service_user", label: "Service User (if any)", type: "text" },
      { name: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"] },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "assigned_to", label: "Assigned To", type: "text" },
      { name: "resolution", label: "Resolution / Response", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["draft", "open", "in_progress", "closed"], required: true },
    ],
    columns: [
      { key: "kind", label: "Kind", status: "kind" },
      { key: "received_date", label: "Received" },
      { key: "source", label: "From" },
      { key: "subject", label: "Subject" },
      { key: "priority", label: "Priority" },
      { key: "assigned_to", label: "Assigned" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  outcomes: {
    key: "outcomes",
    label: "Service User Outcomes",
    short: "Outcomes",
    icon: Target,
    group: "Clinical",
    fields: [
      { name: "client_name", label: "Service User", type: "text", required: true },
      { name: "outcome_area", label: "Outcome Area", type: "select", options: ["wellbeing", "independence", "social", "health", "activity", "goals"], required: true },
      { name: "goal", label: "Goal", type: "text", required: true },
      { name: "target_date", label: "Target Date", type: "date" },
      { name: "progress", label: "Progress Notes", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["planned", "in_progress", "achieved", "not_achieved"], required: true },
    ],
    columns: [
      { key: "client_name", label: "Service User" },
      { key: "outcome_area", label: "Area" },
      { key: "goal", label: "Goal" },
      { key: "target_date", label: "Target" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  maintenance: {
    key: "maintenance",
    label: "Maintenance & Health/Safety",
    short: "Maintenance",
    icon: Wrench,
    group: "Facilities",
    fields: [
      { name: "raised_date", label: "Raised Date", type: "date", required: true },
      { name: "location", label: "Location / Room", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: ["repair", "fire_safety", "electrical", "plumbing", "moving_handling", "environmental", "other"], required: true },
      { name: "priority", label: "Priority", type: "select", options: ["low", "medium", "high", "urgent"], required: true },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "assigned_to", label: "Assigned To", type: "text" },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["open", "in_progress", "completed", "overdue"], required: true },
    ],
    columns: [
      { key: "raised_date", label: "Raised" },
      { key: "location", label: "Location" },
      { key: "category", label: "Category" },
      { key: "priority", label: "Priority", status: "severity" },
      { key: "due_date", label: "Due" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  staff: {
    key: "staff",
    label: "Staff (deprecated — see Staff & Users)",
    short: "Staff",
    icon: Users,
    group: "People",
    hidden: true,
    fields: [
      { name: "full_name", label: "Full Name", type: "text", required: true },
      { name: "role", label: "Job Role", type: "text", required: true },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["active", "on_leave", "left"], required: true },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "full_name", label: "Full Name" },
      { key: "role", label: "Role" },
      { key: "email", label: "Email" },
      { key: "start_date", label: "Started" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  clients: {
    key: "clients",
    label: "Service Users",
    short: "Service Users",
    icon: HeartHandshake,
    group: "People",
    fields: [
      { name: "full_name", label: "Full Name", type: "text", required: true },
      { name: "preferred_name", label: "Preferred Name", type: "text" },
      { name: "site_id", label: "Site / House", type: "site_select" },
      { name: "dob", label: "Date of Birth", type: "date" },
      { name: "primary_needs", label: "Primary Needs", type: "primary_needs_multiselect" },
      { name: "care_plan_summary", label: "Care Plan Summary", type: "richtext" },
      { name: "allergies", label: "Allergies / Warnings", type: "allergy_multiselect" },
      { name: "__next_of_kin", label: "Next of Kin", type: "next_of_kin" },
      { name: "status", label: "Status", type: "select", options: ["active", "respite", "hospitalised", "discharged"], required: true },
    ],
    columns: [
      { key: "full_name", label: "Full Name" },
      { key: "dob", label: "DOB" },
      { key: "primary_needs", label: "Primary Needs", render: "pills" },
      { key: "allergies", label: "Warnings", render: "allergy_pills" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
};

export const MODULE_LIST = Object.values(MODULES);

export const NAV = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  ...MODULE_LIST
    .filter((m) => !m.hidden)
    .map((m) => ({
      path: `/${m.key}`,
      label: m.short,
      fullLabel: m.label,
      icon: m.icon,
      group: m.group,
      testid: `nav-${m.key}`,
    })),
];
